import { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search, User, Calendar, Home, Building2, Loader2, X } from 'lucide-react';
import { useGlobalSearch } from '@/hooks/api/useSearch';
import { cn } from '@/lib/utils';

interface SearchCommandPaletteProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ResultItem {
  id: string;
  type: 'guest' | 'booking' | 'unit' | 'company';
  label: string;
  sublabel: string;
  path: string;
}

export function SearchCommandPalette({ isOpen, onClose }: SearchCommandPaletteProps) {
  const [query, setQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const [focusedIndex, setFocusedIndex] = useState(0);
  const inputRef = useRef<HTMLInputElement>(null);
  const listRef = useRef<HTMLDivElement>(null);
  const navigate = useNavigate();

  // Debounce the query
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedQuery(query.trim());
    }, 300);
    return () => clearTimeout(timer);
  }, [query]);

  const { data, isLoading } = useGlobalSearch(debouncedQuery);

  // Flatten results into a single navigable list
  const flatResults = useMemo((): ResultItem[] => {
    if (!data) return [];
    const items: ResultItem[] = [];

    for (const guest of data.guests) {
      items.push({
        id: guest.id,
        type: 'guest',
        label: guest.fullName,
        sublabel: guest.email ?? guest.phone ?? '',
        path: '/guests',
      });
    }

    for (const booking of data.bookings) {
      const unitName = booking.unit?.name ?? 'Unknown unit';
      const guestName = booking.guest?.fullName ?? '';
      const dateRange = `${booking.checkIn} - ${booking.checkOut}`;
      items.push({
        id: booking.id,
        type: 'booking',
        label: guestName ? `${unitName} -- ${guestName}` : unitName,
        sublabel: `${booking.status} | ${dateRange}`,
        path: '/reservations',
      });
    }

    for (const unit of data.units) {
      items.push({
        id: unit.id,
        type: 'unit',
        label: unit.name,
        sublabel: unit.type,
        path: '/properties',
      });
    }

    for (const company of data.companies) {
      items.push({
        id: company.id,
        type: 'company',
        label: company.name,
        sublabel: company.accountCode,
        path: '/companies',
      });
    }

    return items;
  }, [data]);

  // Reset state when opening/closing
  useEffect(() => {
    if (isOpen) {
      setQuery('');
      setDebouncedQuery('');
      setFocusedIndex(0);
      // Focus the input after the modal renders
      requestAnimationFrame(() => {
        inputRef.current?.focus();
      });
    }
  }, [isOpen]);

  // Reset focused index when results change
  useEffect(() => {
    setFocusedIndex(0);
  }, [flatResults.length]);

  // Scroll focused item into view
  useEffect(() => {
    if (!listRef.current) return;
    const focusedEl = listRef.current.querySelector('[data-focused="true"]');
    if (focusedEl) {
      focusedEl.scrollIntoView({ block: 'nearest' });
    }
  }, [focusedIndex]);

  const selectResult = useCallback(
    (item: ResultItem) => {
      onClose();
      navigate(item.path);
    },
    [onClose, navigate],
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      switch (e.key) {
        case 'ArrowDown':
          e.preventDefault();
          setFocusedIndex((prev) => (prev < flatResults.length - 1 ? prev + 1 : 0));
          break;
        case 'ArrowUp':
          e.preventDefault();
          setFocusedIndex((prev) => (prev > 0 ? prev - 1 : flatResults.length - 1));
          break;
        case 'Enter':
          e.preventDefault();
          if (flatResults[focusedIndex]) {
            selectResult(flatResults[focusedIndex]);
          }
          break;
        case 'Escape':
          e.preventDefault();
          onClose();
          break;
      }
    },
    [flatResults, focusedIndex, onClose, selectResult],
  );

  if (!isOpen) return null;

  const hasQuery = debouncedQuery.length >= 2;
  const hasResults = flatResults.length > 0;

  // Group results by type for section headers
  const groupedSections: Array<{ type: ResultItem['type']; label: string; items: ResultItem[]; startIndex: number }> = [];
  let currentIndex = 0;
  const typeConfig: Record<ResultItem['type'], { label: string; Icon: typeof User }> = {
    guest: { label: 'Guests', Icon: User },
    booking: { label: 'Bookings', Icon: Calendar },
    unit: { label: 'Units', Icon: Home },
    company: { label: 'Companies', Icon: Building2 },
  };

  for (const type of ['guest', 'booking', 'unit', 'company'] as const) {
    const items = flatResults.filter((r) => r.type === type);
    if (items.length > 0) {
      groupedSections.push({ type, label: typeConfig[type].label, items, startIndex: currentIndex });
      currentIndex += items.length;
    }
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-start justify-center pt-[15vh]"
      role="dialog"
      aria-modal="true"
      aria-label="Global search"
    >
      {/* Backdrop */}
      <div
        className="absolute inset-0 bg-black/40 backdrop-blur-sm"
        aria-hidden="true"
        onClick={onClose}
      />

      {/* Palette */}
      <div
        className="relative w-full max-w-lg mx-4 bg-white rounded-xl shadow-2xl border border-gray-200 overflow-hidden"
        onKeyDown={handleKeyDown}
      >
        {/* Search input */}
        <div className="flex items-center gap-3 px-4 py-3 border-b border-gray-200">
          <Search size={18} className="text-gray-400 shrink-0" aria-hidden="true" />
          <input
            ref={inputRef}
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder="Search guests, bookings, units..."
            className="flex-1 text-sm text-gray-900 placeholder-gray-400 bg-transparent outline-none"
            aria-label="Search"
            aria-autocomplete="list"
            aria-controls="search-results"
            aria-activedescendant={flatResults[focusedIndex] ? `search-result-${flatResults[focusedIndex].id}` : undefined}
            role="combobox"
            aria-expanded={hasResults}
          />
          {query && (
            <button
              type="button"
              onClick={() => setQuery('')}
              aria-label="Clear search"
              className="p-1 rounded text-gray-400 hover:text-gray-600 transition-colors"
            >
              <X size={16} aria-hidden="true" />
            </button>
          )}
          <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono text-gray-400 bg-gray-100 rounded border border-gray-200">
            ESC
          </kbd>
        </div>

        {/* Results area */}
        <div
          id="search-results"
          ref={listRef}
          role="listbox"
          className="max-h-[50vh] overflow-y-auto"
        >
          {/* Empty state: no query yet */}
          {!hasQuery && !isLoading && (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-gray-400">Type to search...</p>
            </div>
          )}

          {/* Loading */}
          {isLoading && hasQuery && (
            <div className="flex items-center justify-center px-4 py-8">
              <Loader2 size={20} className="animate-spin text-primary-500" aria-label="Searching" />
              <span className="ml-2 text-sm text-gray-500">Searching...</span>
            </div>
          )}

          {/* No results */}
          {hasQuery && !isLoading && !hasResults && (
            <div className="px-4 py-8 text-center">
              <p className="text-sm text-gray-500">No results found for &ldquo;{debouncedQuery}&rdquo;</p>
            </div>
          )}

          {/* Grouped results */}
          {hasResults &&
            !isLoading &&
            groupedSections.map((section) => {
              const SectionIcon = typeConfig[section.type].Icon;
              return (
                <div key={section.type}>
                  <div className="px-4 pt-3 pb-1 flex items-center gap-2">
                    <SectionIcon size={14} className="text-gray-400" aria-hidden="true" />
                    <span className="text-xs font-semibold text-gray-400 uppercase tracking-wider">
                      {section.label}
                    </span>
                  </div>
                  {section.items.map((item, idx) => {
                    const globalIdx = section.startIndex + idx;
                    const isFocused = globalIdx === focusedIndex;
                    return (
                      <div
                        key={item.id}
                        id={`search-result-${item.id}`}
                        role="option"
                        aria-selected={isFocused}
                        data-focused={isFocused}
                        onClick={() => selectResult(item)}
                        onMouseEnter={() => setFocusedIndex(globalIdx)}
                        className={cn(
                          'flex items-center gap-3 px-4 py-2 cursor-pointer transition-colors',
                          isFocused
                            ? 'bg-primary-50 text-primary-900'
                            : 'text-gray-700 hover:bg-gray-50',
                        )}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{item.label}</p>
                          {item.sublabel && (
                            <p className="text-xs text-gray-400 truncate">{item.sublabel}</p>
                          )}
                        </div>
                        {isFocused && (
                          <kbd className="hidden sm:inline-flex items-center px-1.5 py-0.5 text-[10px] font-mono text-gray-400 bg-gray-100 rounded border border-gray-200">
                            Enter
                          </kbd>
                        )}
                      </div>
                    );
                  })}
                </div>
              );
            })}
        </div>

        {/* Footer hint */}
        {hasResults && (
          <div className="px-4 py-2 border-t border-gray-100 flex items-center gap-4 text-[10px] text-gray-400">
            <span>
              <kbd className="px-1 py-0.5 font-mono bg-gray-100 rounded border border-gray-200 mr-1">
                &uarr;&darr;
              </kbd>
              Navigate
            </span>
            <span>
              <kbd className="px-1 py-0.5 font-mono bg-gray-100 rounded border border-gray-200 mr-1">
                Enter
              </kbd>
              Open
            </span>
            <span>
              <kbd className="px-1 py-0.5 font-mono bg-gray-100 rounded border border-gray-200 mr-1">
                Esc
              </kbd>
              Close
            </span>
          </div>
        )}
      </div>
    </div>
  );
}
